import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import "../../RessourcesStyles/DemanderFormation.css";

import DemandeFormModal from "./DemandeFormModal";
import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";

import { API_BASE_URL } from "../../../../../../api";
import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import {
    createDemandeCollabRequest,
    createDemandeManagerBulkRequest,
    getCompanyDemandesRequest,
    getMyFormationDemandesRequest,
} from "../../../../../redux/actions/demandesActions";
import { getDirectReportsRequest } from "../../../../../redux/actions/userActions";
import {
    fetchFormationBookmarksRequest,
    toggleFormationBookmarkRequest,
} from "../../../../../redux/actions/formationBookmarksActions";
import {
    Bookmark,
    Calendar,
    ClipboardCheck,
    Eye,
    Target,
    Users,
    X,
    Clock,
} from "lucide-react";

const isFormationCampaign = (c) => String(c?.typeCampagne || "").toUpperCase() === "FORMATION";
const isCampaignActive = (c) => c?.status === true;
const isDemandeInactive = (status) => ["REJECTED", "CANCELLED", "WITHDRAWN"].includes(String(status || "").toUpperCase());

export default function FormationBookmarksPage({ role = "collaborateur" }) {
    const isManager = role === "manager";
    const dispatch = useDispatch();
    const uid = sessionStorage.getItem("uid");

    const { visibleList: campagnesRaw = [], loading: campagnesLoading } = useSelector((s) => s.campagnes || {});
    const { myFormationDemandes = [], companyDemandes = [] } = useSelector((s) => s.demandes || {});
    const { directReports = [] } = useSelector((s) => s.user || {});
    const { bookmarksByCampagneId = {}, loading: bookmarksLoading } = useSelector((s) => s.formationBookmarks || {});

    const campagnes = useMemo(
        () => (campagnesRaw || []).filter((c) => isFormationCampaign(c) && isCampaignActive(c)),
        [campagnesRaw]
    );

    const [campagneIdLocal, setCampagneIdLocal] = useState("");
    const campagneId = campagneIdLocal ? Number(campagneIdLocal) : null;
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [detailsFormation, setDetailsFormation] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);
    const [openUsersPick, setOpenUsersPick] = useState(false);
    const [openFieldsModal, setOpenFieldsModal] = useState(false);
    const [pickedUsers, setPickedUsers] = useState([]);

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        if (isManager) {
            dispatch(getCompanyDemandesRequest());
            if (uid) dispatch(getDirectReportsRequest(uid));
        } else {
            dispatch(getMyFormationDemandesRequest());
        }
    }, [dispatch, isManager, uid]);

    useEffect(() => {
        if (!campagneIdLocal && campagnes?.length) {
            const defaultCampagne = campagnes.find((c) => c?.isDefault) || campagnes[0];
            setCampagneIdLocal(String(defaultCampagne.id));
        }
    }, [campagnes, campagneIdLocal]);

    useEffect(() => {
        if (campagneId) {
            dispatch(fetchFormationBookmarksRequest(campagneId));
        }
    }, [dispatch, campagneId]);

    const selectedCampagne = useMemo(
        () => (campagnes || []).find((c) => String(c.id) === String(campagneId)) || null,
        [campagnes, campagneId]
    );

    const bookmarkedFormations = useMemo(
        () => bookmarksByCampagneId[String(campagneId)] || [],
        [bookmarksByCampagneId, campagneId]
    );

    const activeDemandesForCampagne = useMemo(() => {
        const source = isManager ? companyDemandes : myFormationDemandes;
        return (source || []).filter((d) => String(d?.campagneId) === String(campagneId) && !isDemandeInactive(d?.status));
    }, [companyDemandes, myFormationDemandes, campagneId, isManager]);

    const requestedFormationIds = useMemo(
        () => new Set(activeDemandesForCampagne.map((d) => String(d?.formationId)).filter(Boolean)),
        [activeDemandesForCampagne]
    );

    const hasReachedQuota = useMemo(() => {
        if (!selectedCampagne?.nombreMaxFormations) return false;
        return activeDemandesForCampagne.length >= Number(selectedCampagne.nombreMaxFormations);
    }, [activeDemandesForCampagne.length, selectedCampagne]);

    const getCollabActionState = (formationId) => {
        const alreadyRequested = requestedFormationIds.has(String(formationId));
        const disabled = alreadyRequested || hasReachedQuota || !isCampaignActive(selectedCampagne);
        const label = alreadyRequested
            ? "Déjà demandée"
            : hasReachedQuota
                ? "Quota atteint"
                : !isCampaignActive(selectedCampagne)
                    ? "Campagne inactive"
                    : "Demander";
        return { disabled, label };
    };

    const getUserDemandesForCampagne = (userId) =>
        activeDemandesForCampagne.filter((d) => String(d?.targetUserId) === String(userId));

    const canUserRequestFormation = (userId, formationId) => {
        const userDemandes = getUserDemandesForCampagne(userId);
        const alreadyRequested = userDemandes.some((d) => String(d?.formationId) === String(formationId));
        const quotaReached = selectedCampagne?.nombreMaxFormations
            ? userDemandes.length >= Number(selectedCampagne.nombreMaxFormations)
            : false;

        return { alreadyRequested, quotaReached, eligible: !alreadyRequested && !quotaReached };
    };

    const getEligibleUsersForFormation = (formationId) =>
        (directReports || []).filter((u) => canUserRequestFormation(u?.id, formationId).eligible);

    const getUnavailableUserIdsForFormation = (formationId) =>
        (directReports || [])
            .filter((u) => !canUserRequestFormation(u?.id, formationId).eligible)
            .map((u) => u.id);

    const getManagerActionState = (formationId) => {
        if (!isCampaignActive(selectedCampagne)) {
            return { disabled: true, label: "Campagne inactive", helper: "Cette campagne n'est pas active." };
        }
        const eligibleUsers = getEligibleUsersForFormation(formationId);
        if (eligibleUsers.length === 0) {
            return {
                disabled: true,
                label: "Déjà demandée",
                helper: "Tous les collaborateurs éligibles ont déjà cette formation ou ont atteint leur quota.",
            };
        }
        return { disabled: false, label: "Demander", helper: `${eligibleUsers.length} collaborateur(s) éligible(s)` };
    };

    const getActionState = (formationId) => isManager ? getManagerActionState(formationId) : getCollabActionState(formationId);

    const openDemande = (formation) => {
        if (!campagneId) return Swal.fire({ icon: "warning", text: "Sélectionne d'abord une campagne." });
        const actionState = getActionState(formation?.id || formation?.formationId);
        if (actionState.disabled) {
            if (actionState.helper || actionState.label === "Quota atteint") {
                Swal.fire({
                    icon: "warning",
                    text: actionState.helper || "Vous avez atteint le nombre maximum de demandes autorisées pour cette campagne",
                });
            }
            return;
        }

        setSelectedFormation(formation);
        if (isManager) {
            setOpenUsersPick(true);
        } else {
            setOpenModal(true);
        }
    };

    const submitDemande = (payloadFromModal) => {
        const payload = {
            ...payloadFromModal,
            campagneId,
            formationId: selectedFormation?.id || selectedFormation?.formationId || payloadFromModal.formationId,
            type: "FORMATION",
        };
        dispatch(createDemandeCollabRequest(payload));
        setOpenModal(false);
        setSelectedFormation(null);
    };

    const onConfirmUsers = (ids) => {
        const uniqueIds = Array.from(new Set(ids || []));
        const formationId = selectedFormation?.id || selectedFormation?.formationId;
        const selected = (directReports || []).filter(
            (u) => uniqueIds.includes(u.id) && canUserRequestFormation(u.id, formationId).eligible
        );
        setPickedUsers(selected);
        setOpenUsersPick(false);
        setOpenFieldsModal(true);
    };

    const onSubmitFields = (rows) => {
        const formationId = selectedFormation?.id || selectedFormation?.formationId;
        if (!formationId) return Swal.fire({ icon: "warning", text: "Formation introuvable." });
        dispatch(createDemandeManagerBulkRequest({
            campagneId: Number(campagneId),
            type: "FORMATION",
            formationId: Number(formationId),
            items: (rows || []).map((r) => ({
                targetUserId: Number(r.targetUserId),
                mode: r.mode || null,
                period: r.period || null,
                niveau: r.niveau || null,
                certifiante: !!r.certifiante,
                priorite: r.priorite != null ? Number(r.priorite) : null,
                objectifs: r.objectifs || null,
                objectifs2: r.objectifs2 || null,
                objectifs3: r.objectifs3 || null,
            })),
        }));
        setOpenFieldsModal(false);
        setPickedUsers([]);
        setSelectedFormation(null);
    };

    const toggleBookmark = (event, formation) => {
        event?.stopPropagation();
        dispatch(toggleFormationBookmarkRequest({
            campagneId,
            formationId: formation?.id || formation?.formationId,
            formation,
        }));
    };

    const loading = campagnesLoading || bookmarksLoading;

    return (
        <div className="dfPage">
            <div className="dfWrap">
                <div className="dfHeader">
                    <div>
                        <h1 className="dfTitle">Mes formations sauvegardées</h1>
                    </div>
                </div>

                <div className="dfCampaignBar">
                    <label className="dfCampaignLabel">Campagne de formation</label>
                    <div className="dfSelectWrapper">
                        <Calendar className="dfSelectIcon" size={16} />
                        <select
                            className="dfSelect"
                            value={campagneIdLocal}
                            onChange={(e) => setCampagneIdLocal(e.target.value)}
                            disabled={!!campagnesLoading}
                        >
                            <option value="">— Sélectionnez une campagne —</option>
                            {campagnes.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.nom || c.name} {c.anneeCampagne ? `(${c.anneeCampagne})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="dfCard">
                    {!campagneId ? (
                        <div className="ae-empty">
                            <Calendar size={40} className="ae-empty-icon" />
                            <h3>Aucune campagne active</h3>
                            <p>Veuillez sélectionner une campagne de formation.</p>
                        </div>
                    ) : loading && bookmarkedFormations.length === 0 ? (
                        <div className="dfEmpty">Chargement des formations sauvegardées...</div>
                    ) : bookmarkedFormations.length === 0 ? (
                        <div className="dfEmpty">Aucune formation sauvegardée pour cette campagne.</div>
                    ) : (
                        <div className="dfFormationGrid">
                            {bookmarkedFormations.map((formation) => (
                                <BookmarkFormationCard
                                    key={formation.formationId || formation.id}
                                    formation={formation}
                                    actionState={getActionState(formation.id || formation.formationId)}
                                    isManager={isManager}
                                    onDemande={openDemande}
                                    onDetails={(f) => { setDetailsFormation(f); setOpenDetails(true); }}
                                    onToggleBookmark={toggleBookmark}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <DemandeFormModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSubmit={submitDemande}
                formation={selectedFormation}
                campagneId={campagneId}
                type="FORMATION"
            />

            <UsersPickerModal
                open={openUsersPick}
                onClose={() => setOpenUsersPick(false)}
                users={directReports}
                title="Sélectionner les collaborateurs de votre équipe"
                onConfirm={onConfirmUsers}
                assignedIds={getUnavailableUserIdsForFormation(selectedFormation?.id || selectedFormation?.formationId)}
            />

            <BulkUsersFieldsModal
                open={openFieldsModal}
                onClose={() => setOpenFieldsModal(false)}
                users={pickedUsers}
                title="Paramétrage par collaborateur"
                onSubmit={onSubmitFields}
            />

            {openDetails && detailsFormation && (
                <FormationDetailsModal
                    formation={detailsFormation}
                    actionState={getActionState(detailsFormation.id || detailsFormation.formationId)}
                    isManager={isManager}
                    onClose={() => setOpenDetails(false)}
                    onDemande={() => {
                        setOpenDetails(false);
                        openDemande(detailsFormation);
                    }}
                />
            )}
        </div>
    );
}

function BookmarkFormationCard({ formation, actionState, isManager, onDemande, onDetails, onToggleBookmark }) {
    const nom = formation.nom || formation.titre || formation.name;
    const picture = formation.picture || formation.image;
    const duration = formation.duration || formation.duree;

    return (
        <div className="dfFormationCard">
            <div className="dfFormationMedia" onClick={() => onDetails(formation)}>
                {picture ? (
                    <img src={`${API_BASE_URL}/api/formations/images/${picture}`} alt={nom} />
                ) : (
                    <div className="dfFormationPlaceholder">
                        {(nom || "F").slice(0, 1).toUpperCase()}
                    </div>
                )}
                <div className="dfFormationOverlay">
                    <span className="dfOverlayBtn">Détails</span>
                </div>
            </div>

            <div className="dfFormationBody">
                <div className="dfFormationTitle" title={nom}>{nom}</div>
                <div className="dfFormationInfo">
                    <div className="dfInfoRow"><span>Domaine :</span> {formation.domaineNom || formation.domaine || "—"}</div>
                    <div className="dfInfoRow"><span>Thème :</span> {formation.themeNom || formation.theme || "—"}</div>
                    <div className="dfInfoRow"><span>Mode :</span> {formation.type || formation.mode || "—"}</div>
                    <div className="dfInfoRow"><span>Durée :</span> {duration ? `${duration} ${formation.typeDuree || "jour(s)"}` : "—"}</div>
                </div>
                <div className="dfFormationFooter">
                    <button
                        type="button"
                        className={`dfSubmitBtn ${isManager ? "dfrf-submitBtn" : ""}`}
                        disabled={actionState?.disabled}
                        onClick={() => onDemande(formation)}
                    >
                        {isManager && <Users size={14} style={{ marginRight: 6 }} />}
                        {actionState?.label || "Demander"}
                    </button>
                    <div className="dfCardActions">
                        <button
                            type="button"
                            className="dfActionBtn is-bookmarked"
                            title="Sauvegarder"
                            onClick={(event) => onToggleBookmark(event, formation)}
                        >
                            <Bookmark size={18} fill="currentColor" />
                        </button>
                        <button
                            type="button"
                            className="dfActionBtn"
                            onClick={() => onDetails(formation)}
                            title="Détails"
                        >
                            <Eye size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function FormationDetailsModal({ formation, actionState, isManager, onClose, onDemande }) {
    const nom = formation.nom || formation.titre || formation.name;
    const picture = formation.picture || formation.image;

    return (
        <div className="dfDetailsModalOverlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="dfDetailsModal">
                <div className="dfDetailsBanner">
                    {picture ? (
                        <img src={`${API_BASE_URL}/api/formations/images/${picture}`} alt={nom} />
                    ) : (
                        <div className="dfDetailsPlaceholderBanner">
                            <span>{(nom || "F").slice(0, 1).toUpperCase()}</span>
                        </div>
                    )}
                    <div className="dfDetailsBadgeTop">{formation.type || formation.mode || "Formation"}</div>
                    <button type="button" className="dfDetailsClose" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="dfDetailsBody">
                    <h2 className="dfDetailsTitle">{nom}</h2>
                    <div className="dfDetailsTitleUnderline" />
                    <div className="dfDetailsGrid">
                        <div className="dfDetailsMain">
                            <section className="dfDetailsSection">
                                <div className="dfSectionHeader">
                                    <div className="dfSectionIcon"><Target size={20} /></div>
                                    <h3>Objectif(s)</h3>
                                </div>
                                <ul className="dfDetailsList">
                                    {(formation.objectif || formation.objectifs) ? (
                                        String(formation.objectif || formation.objectifs).split(/[\n;]+/).map((item, index) => (
                                            <li key={index}>{item.trim()}</li>
                                        ))
                                    ) : (
                                        <li className="dfNoBullet dfMuted">Aucun objectif spécifié</li>
                                    )}
                                </ul>
                            </section>
                            <section className="dfDetailsSection">
                                <div className="dfSectionHeader">
                                    <div className="dfSectionIcon"><ClipboardCheck size={20} /></div>
                                    <h3>Prérequis</h3>
                                </div>
                                <ul className="dfDetailsList">
                                    {formation.prerequis ? (
                                        String(formation.prerequis).split(/[\n;]+/).map((item, index) => (
                                            <li key={index}>{item.trim()}</li>
                                        ))
                                    ) : (
                                        <li className="dfNoBullet dfMuted">Aucun prérequis spécifique</li>
                                    )}
                                </ul>
                            </section>
                        </div>
                        <aside className="dfDetailsSidebar">
                            <div className="dfInfoCard">
                                <h4>Informations pratiques</h4>
                                <div className="dfInfoItem">
                                    <Users size={18} />
                                    <div>
                                        <label>Public visé</label>
                                        <span>{formation.publiqueVise || (isManager ? "Équipe / Tous" : "Tous les collaborateurs")}</span>
                                    </div>
                                </div>
                                <div className="dfInfoItem">
                                    <Clock size={18} />
                                    <div>
                                        <label>Durée</label>
                                        <span>{formation.duration || formation.duree || "—"} {formation.typeDuree || "jour(s)"}</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="dfDetailsActionBtn"
                                disabled={actionState?.disabled}
                                onClick={onDemande}
                            >
                                {isManager && <Users size={16} style={{ marginRight: 8 }} />}
                                {actionState?.label || "Demander"}
                            </button>
                        </aside>
                    </div>
                </div>
            </div>
        </div>
    );
}
